/**
 * HoldDialog (UX6) — park a document with a comment addressed to a named person.
 *
 * The assignee picker is the tenant roster filtered to people who can act on THIS document type:
 * users whose analyzerScope includes the document type (or is unrestricted), plus admins
 * (decision 2/3). On confirm it POSTs to /api/documents/:id/hold; the backend sets ON_HOLD,
 * clears the review lock, and the recipient sees it in their "Needs my attention" list.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { authFetch } from '../../services/authFetch';

const MAX_COMMENT_LENGTH = 2000;

interface RosterUser {
  userId: string;
  displayName: string;
  email: string;
  role: string;
  analyzerScope?: string[] | 'all';
  isActive?: boolean;
}

interface HoldDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  /** The document's type — scopes the assignee picker to people who can act on it. */
  documentType: string;
  /** Called after the hold is saved successfully (e.g. to leave the workspace). */
  onHeld: () => void;
}

/** A user can be an assignee if they're an admin or their model scope covers this document type. */
function canReview(user: RosterUser, documentType: string): boolean {
  if (user.role === 'TenantAdmin' || user.role === 'PlatformAdmin') return true;
  const scope = user.analyzerScope;
  if (scope === undefined || scope === 'all') return true; // unrestricted / back-compat
  return scope.includes(documentType);
}

const HoldDialog: React.FC<HoldDialogProps> = ({ open, onClose, documentId, documentType, onHeld }) => {
  const [comment, setComment] = useState('');
  const [assignee, setAssignee] = useState<RosterUser | null>(null);
  const [users, setUsers] = useState<RosterUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [rosterError, setRosterError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load + scope the roster each time the dialog opens; reset the form on close.
  useEffect(() => {
    if (!open) {
      setComment('');
      setAssignee(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoadingUsers(true);
    setRosterError(false);
    (async () => {
      try {
        const response = await authFetch('/users');
        if (!response.ok) throw new Error('roster');
        const json = await response.json();
        if (cancelled) return;
        const all: RosterUser[] = Array.isArray(json.users) ? json.users : [];
        setUsers(all.filter((u) => u.isActive !== false && canReview(u, documentType)));
      } catch {
        if (!cancelled) setRosterError(true);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, documentType]);

  const trimmed = comment.trim();
  const canSubmit = useMemo(
    () => !!trimmed && trimmed.length <= MAX_COMMENT_LENGTH && !!assignee && !submitting,
    [trimmed, assignee, submitting]
  );

  const handleConfirm = async () => {
    if (!assignee || !trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await authFetch(`/documents/${encodeURIComponent(documentId)}/hold`, {
        method: 'POST',
        body: JSON.stringify({ comment: trimmed, assignedTo: assignee.userId }),
      });
      if (response.ok) {
        onHeld();
        return;
      }
      const json = await response.json().catch(() => ({}));
      setError(json.error || 'Could not put this document on hold. Please try again.');
    } catch {
      setError('Network error — could not put this document on hold.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="hold-dialog-title">
      <DialogTitle id="hold-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PauseCircleOutlineIcon color="warning" aria-hidden="true" />
          Put document on hold
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {rosterError && (
            <Alert severity="warning">Couldn’t load the people list. Close and try again in a moment.</Alert>
          )}

          <Autocomplete
            options={users}
            loading={loadingUsers}
            value={assignee}
            onChange={(_e, value) => setAssignee(value)}
            getOptionLabel={(u) => `${u.displayName}${u.email ? ` (${u.email})` : ''}`}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            noOptionsText="No one with access to this document type"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Send to"
                required
                helperText="The person who should look at this document next"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Comment"
            required
            multiline
            minRows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
            helperText={`What needs attention? ${comment.length}/${MAX_COMMENT_LENGTH}`}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disableElevation
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={18} /> : <PauseCircleOutlineIcon />}
        >
          {submitting ? 'Putting on hold…' : 'Put on hold'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HoldDialog;
